import React, { useState, useEffect } from 'react';
import { Space, Typography, Avatar, notification, Tooltip } from 'antd';
import { UserOutlined, SaveOutlined, CheckOutlined, CloseOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/auth';
import { FEATURES_LIST, isSuperAdmin, RESOURCE_TYPE_CODE_TO_KEY, RESOURCE_KEY_TO_TYPE_CODE, FEATURE, ACTION, FEATURE_KEY_TO_CODE, ACTION_KEY_TO_CODE } from '../../helpers/permissions';
import { roles } from '../../helpers/roles';
import { formatDate } from '../../helpers/datas';
import Api from '../../services/api';
import ProjectService from '../../services/projectService';
import SynchronizationService from '../../services/synchronizationService';
import { useProjects } from '../../hooks/useProjects';
import { getErpName, getSyncStatus } from '../../helpers/synchronizations';


// Componentes personalizados
import Modal from './Modal';
import Card from '../card/Card';
import { Button, Select, Tag, Alert, Collapse, SwitchInput, Toast } from '../ui';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const UserPermissionsModal = ({ 
  open, 
  onCancel, 
  selectedUser, 
  onSave,
  onUsersUpdate 
}) => {
  const RUN_INCREMENTAL_PERMISSION_NAME = 'RUN_INCREMENTAL';
  const RUN_INCREMENTAL_PERMISSION_FEATURE = 12;
  const RUN_INCREMENTAL_PERMISSION_ACTION = 11;
  const { theme } = useTheme();
  const { 
    isAdmin,
    getUserFeatureActions,
    getUserInfo,
    hasModule
  } = useAuth();
  const { projects, loading: projectsLoading } = useProjects();
  const [userPermissions, setUserPermissions] = useState({});
  const [granularPermissions, setGranularPermissions] = useState({
    reports: [],
    integrations: [],
    dashboards: [],
    projects: []
  });

  const [userProjects, setUserProjects] = useState([]);
  const [selectedRole, setSelectedRole] = useState('RoleUser'); // Estado para o tipo de usuário
  const [originalRole, setOriginalRole] = useState('RoleUser'); // Estado para o role original do usuário
  
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableReports, setAvailableReports] = useState([]);
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);

  const UI_STUDIO_DISABLED = "ui:module:studio:disabled";
  const UI_ACADEMY_DISABLED = "ui:module:academy:disabled";
  const UI_GESTAO_RESTRICTED = "ui:gestao:restricted";
  const GESTAO_MENUS = [
    { key: "cashflow", label: "Fluxo de Caixa" },
    { key: "payrec", label: "A Pagar & Receber" },
    { key: "dredfc", label: "DRE/DFC" },
    { key: "balances", label: "Saldos" },
    { key: "indicators", label: "Indicadores" },
    { key: "sales", label: "Vendas" },
    { key: "pdf_exports", label: "Exportação de PDF" },
  ];
  const buildGestaoMenuId = (menuKey) =>
    `ui:gestao:${String(menuKey || "").trim().toLowerCase()}`;
  const tenantHasStudio = typeof hasModule === "function" ? hasModule("studio") : false;
  const reportUiIds = Array.isArray(granularPermissions?.reports)
    ? granularPermissions.reports
    : [];
  const studioAllowed = !reportUiIds.includes(UI_STUDIO_DISABLED);
  const academyAllowed = !reportUiIds.includes(UI_ACADEMY_DISABLED);
  const gestaoRestricted = reportUiIds.includes(UI_GESTAO_RESTRICTED);

  const updateReportUiPermissions = (updater) => {
    setGranularPermissions((prev) => {
      const current = Array.isArray(prev?.reports) ? prev.reports : [];
      const next = updater(current);
      return { ...(prev || {}), reports: Array.isArray(next) ? next : current };
    });
    setHasChanges(true);
  };

  const clearGestaoMenuRestrictions = (current) =>
    (Array.isArray(current) ? current : []).filter(
      (id) =>
        id !== UI_GESTAO_RESTRICTED &&
        !String(id).toLowerCase().startsWith("ui:gestao:"),
    );

  const getGestaoMenuEnabled = (menuKey) => {
    if (!gestaoRestricted) return true;
    const id = buildGestaoMenuId(menuKey);
    return reportUiIds.includes(id);
  };

  const setGestaoMenuEnabled = (menuKey, value) => {
    const key = String(menuKey || "").trim().toLowerCase();
    if (!key) return;
    const id = buildGestaoMenuId(key);
    const allIds = GESTAO_MENUS.map((m) => buildGestaoMenuId(m.key));
    updateReportUiPermissions((current) => {
      const base = Array.isArray(current) ? current : [];
      const isRestricted = base.includes(UI_GESTAO_RESTRICTED);

      if (!isRestricted) {
        if (value) return base;
        const cleaned = clearGestaoMenuRestrictions(base);
        const seed = allIds.filter((x) => x !== id);
        return [...cleaned, UI_GESTAO_RESTRICTED, ...seed];
      }

      const without = base.filter((x) => x !== id);
      const next = value ? [...without, id] : without;
      const enabledCount = allIds.filter((x) => next.includes(x)).length;
      if (enabledCount === allIds.length) {
        return clearGestaoMenuRestrictions(next);
      }
      return next;
    });
  };

  const toggleAllGestaoMenus = (value) => {
    updateReportUiPermissions((current) => {
      const base = Array.isArray(current) ? current : [];
      const cleaned = clearGestaoMenuRestrictions(base);
      if (value) return cleaned;
      return [...cleaned, UI_GESTAO_RESTRICTED];
    });
  };

  const setStudioAllowed = (value) => {
    updateReportUiPermissions((current) => {
      const base = Array.isArray(current) ? current : [];
      const without = base.filter((x) => x !== UI_STUDIO_DISABLED);
      return value ? without : [...without, UI_STUDIO_DISABLED];
    });
  };

  const setAcademyAllowed = (value) => {
    updateReportUiPermissions((current) => {
      const base = Array.isArray(current) ? current : [];
      const without = base.filter((x) => x !== UI_ACADEMY_DISABLED);
      return value ? without : [...without, UI_ACADEMY_DISABLED];
    });
  };

  const gestaoEnabledCount = GESTAO_MENUS.filter((m) =>
    getGestaoMenuEnabled(m.key),
  ).length;
  const gestaoSwitchState =
    gestaoEnabledCount === GESTAO_MENUS.length
      ? true
      : gestaoEnabledCount > 0
        ? "indeterminate"
        : false;

  useEffect(() => {
    if (open && selectedUser) {
      loadUserPermissions();
      loadAvailableResources();
    }
  }, [open, selectedUser]);

  // Atualizar availableProjects quando os projetos do hook mudarem
  useEffect(() => {
    if (projects && projects.length > 0) {
      setAvailableProjects(projects);
    }
  }, [projects]);



  const loadUserPermissions = async () => {
    try {
      setLoading(true);

      // -------------------- Permissões tradicionais --------------------
      const permissionsMap = {};
      if (Array.isArray(selectedUser.permissions)) {
        selectedUser.permissions.forEach((perm) => {
          const name = perm?.name ?? perm?.Name ?? null;
          const feature = perm?.feature ?? perm?.Feature ?? null;
          const action = perm?.action ?? perm?.Action ?? null;
          if (name) permissionsMap[String(name)] = true;
          if (
            Number(feature) === RUN_INCREMENTAL_PERMISSION_FEATURE &&
            Number(action) === RUN_INCREMENTAL_PERMISSION_ACTION
          ) {
            permissionsMap[RUN_INCREMENTAL_PERMISSION_NAME] = true;
          }
        });
      }

      // Se for super admin, marca tudo
      if (isSuperAdmin(selectedUser)) {
        FEATURES_LIST.forEach((feature) => {
          feature.actions.forEach((action) => {
            permissionsMap[action.id] = true;
          });
        });
      }

      // -------------------- Permissões granulares --------------------
      const granularMap = {
        reports: [],
        integrations: [],
        dashboards: [],
        projects: [],
      };

      if (Array.isArray(selectedUser.granularPermissions)) {
        selectedUser.granularPermissions.forEach((gp) => {
          const key = RESOURCE_TYPE_CODE_TO_KEY[gp.resourceType];
          if (key) {
            granularMap[key].push(gp.resourceId);
          }
        });
      }

      // Adicionar projectId do userProjects em granularPermissions.projects
      if (Array.isArray(selectedUser.userProjects)) {
        granularMap.projects = selectedUser.userProjects.map(p => (p.projectId || p.id || p).toString());
      }

      setUserPermissions(permissionsMap);
      setGranularPermissions(granularMap);
      setHasChanges(false);
      
      // Carregar o role atual do usuário
      if (selectedUser.roles && selectedUser.roles.length > 0) {
        const currentRole = selectedUser.roles[0]?.name || 'RoleUser';
        setSelectedRole(currentRole);
        setOriginalRole(currentRole); // Armazenar o role original
      } else {
        setSelectedRole('RoleUser');
        setOriginalRole('RoleUser'); // Armazenar o role original
      }
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Erro ao carregar permissões do usuário.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableResources = async () => {
    try {
      // Dados mockados para relatórios (manter até ter endpoint real)
      const mockReports = [
        { 
          id: '550e8400-e29b-41d4-a716-446655440001', 
          name: 'Relatório de Vendas Q1 2024',
          type: 'Vendas',
          description: 'Relatório trimestral de vendas'
        },
        { 
          id: '550e8400-e29b-41d4-a716-446655440002', 
          name: 'Relatório Financeiro 2024',
          type: 'Financeiro',
          description: 'Análise financeira anual'
        },
        { 
          id: '550e8400-e29b-41d4-a716-446655440003', 
          name: 'Relatório de Usuários Ativos',
          type: 'Usuários',
          description: 'Estatísticas de usuários ativos'
        },
        { 
          id: '550e8400-e29b-41d4-a716-446655440004', 
          name: 'Análise de Performance',
          type: 'Analytics',
          description: 'Métricas de performance do sistema'
        },
        { 
          id: '550e8400-e29b-41d4-a716-446655440005', 
          name: 'Relatório Customizado - Dashboard',
          type: 'Customizado',
          description: 'Relatório personalizado para dashboard'
        }
      ];

      // Buscar integrações reais do sistema
      // ResourceType = 1 para integrações (conforme RESOURCE_KEY_TO_TYPE_CODE)
      const allSyncs = await SynchronizationService.getSynchronizationsSimplified({
        page: 1,
        pageSize: 1000 // Pegar todas as integrações
      });

      // Transformar sincronizações em formato de integrações para permissões
      const realIntegrations = allSyncs.map(sync => {
        // Buscar nome do projeto se disponível
        const projectName = sync.project?.name || projects?.find(p => p.id === sync.projectId)?.name || 'Projeto Desconhecido';
        const erpName = getErpName(sync.erp);
        const status = getSyncStatus(sync.status);

        return {
          id: String(sync.id), // Garantir que o ID seja string para permissões granulares
          name: sync.nome || `Integração ${erpName} - ${projectName}`,
          type: erpName,
          status: status,
          description: `Integração ${erpName} do projeto ${projectName}`,
          projectId: sync.projectId,
          erp: sync.erp,
          projectName: projectName,
          originalSyncId: sync.id // Manter o ID original para referência
        };
      });

        


      setAvailableReports(mockReports);
      setAvailableIntegrations(realIntegrations);
    } catch (error) {
      console.error('❌ Erro ao carregar recursos:', error);
      
      // Em caso de erro, usar dados mockados como fallback
      const mockIntegrations = [
        { 
          id: '660e8400-e29b-41d4-a716-446655440001', 
          name: 'Integração ERP SAP',
          type: 'ERP',
          status: 'Ativa',
          description: 'Integração com sistema SAP'
        },
        { 
          id: '660e8400-e29b-41d4-a716-446655440002', 
          name: 'Integração CRM Salesforce',
          type: 'CRM',
          status: 'Ativa',
          description: 'Integração com Salesforce'
        }
      ];
      
      setAvailableIntegrations(mockIntegrations);
    }
  };

  const handlePermissionChange = (permissionId, value) => {
    // Não permitir alterar permissões do super admin
    if (isSuperAdmin(selectedUser)) {
      notification.warning({
        message: 'Aviso',
        description: 'Não é possível alterar permissões do administrador principal.',
        placement: 'topRight',
      });
      return;
    }

    setUserPermissions(prev => ({
      ...prev,
      [permissionId]: value
    }));
    setHasChanges(true);
  };

  // Função para ativar/desativar todas as permissões de um módulo
  const handleModulePermissionChange = (feature, value) => {
    // Não permitir alterar permissões do super admin
    if (isSuperAdmin(selectedUser)) {
      notification.warning({
        message: 'Aviso',
        description: 'Não é possível alterar permissões do administrador principal.',
        placement: 'topRight',
      });
      return;
    }

    const newPermissions = { ...userPermissions };
    
    // Ativar/desativar todas as permissões do módulo
    feature.actions.forEach(action => {
      newPermissions[action.id] = value;
    });

    setUserPermissions(newPermissions);
    setHasChanges(true);
  };

  // Função para verificar se todas as permissões de um módulo estão ativas
  const isModuleFullyActive = (feature) => {
    return feature.actions.every(action => userPermissions[action.id]);
  };

  const roleOptions = [
    {
      label: 'Administrador',
      value: 'RoleAdmin',
    },
    {
      label: 'Usuário',
      value: 'RoleUser',
    }
  ];

  // Função para verificar se pelo menos uma permissão do módulo está ativa
  const isModulePartiallyActive = (feature) => {
    return feature.actions.some(action => userPermissions[action.id]);
  };

  // Função para determinar o estado do switch do módulo
  const getModuleSwitchState = (feature) => {
    const allActive = isModuleFullyActive(feature);
    const someActive = isModulePartiallyActive(feature);
    
    if (allActive) return true;
    if (someActive) return 'indeterminate'; // Estado intermediário
    return false;
  };

  // Função para lidar com mudança de role
  const handleRoleChange = (value) => {
    
    setSelectedRole(value);
    setHasChanges(true);
  };

  const handleGranularPermissionChange = (resourceType, resourceIds) => {
    // Não permitir alterar permissões do super admin
    if (isSuperAdmin(selectedUser)) {
      notification.warning({
        message: 'Aviso',
        description: 'Não é possível alterar permissões do administrador principal.',
        placement: 'topRight',
      });
      return;
    }

    setGranularPermissions(prev => ({
      ...prev,
      [resourceType]: resourceIds || []
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      // Converter permissões tradicionais para o formato esperado pelo backend
      const permissions = [];
      Object.entries(userPermissions).forEach(([permissionId, isGranted]) => {
        if (isGranted) {
          if (permissionId === RUN_INCREMENTAL_PERMISSION_NAME) {
            permissions.push({
              Name: RUN_INCREMENTAL_PERMISSION_NAME,
              Feature: RUN_INCREMENTAL_PERMISSION_FEATURE,
              Action: RUN_INCREMENTAL_PERMISSION_ACTION,
            });
            return;
          }
          // O permissionId tem formato "VIEW_USER", "CREATE_USER", etc.
          // Precisamos extrair a action e o feature
          const parts = permissionId.split('_');
          if (parts.length >= 2) {
            const actionPart = parts[0]; // VIEW, CREATE, EDIT, etc.
            const featurePart = parts[1]; // USER, ROLE, COMPANY, etc.
            
            // Mapear strings para códigos numéricos do backend
            const featureNumeric = FEATURE_KEY_TO_CODE[featurePart];
            const actionNumeric = ACTION_KEY_TO_CODE[actionPart];
            if (featureNumeric !== undefined && actionNumeric !== undefined) {
              permissions.push({
                Name: permissionId,
                Feature: featureNumeric,
                Action: actionNumeric
              });
              return;
            }
          }

          permissions.push({ Name: permissionId });
        }
      });

       const granularPermissionsArray = [];
       
       // Usar o mapeamento correto dos resource types
       Object.entries(granularPermissions).forEach(([resourceType, resourceIds]) => {
         const resourceTypeCode = RESOURCE_KEY_TO_TYPE_CODE[resourceType];
         if (resourceTypeCode !== undefined && Array.isArray(resourceIds)) {
           resourceIds.forEach(resourceId => {
             granularPermissionsArray.push({
               ResourceType: resourceTypeCode,
               ResourceId: resourceId
             });
           });
         }
       });

      const roleChanged = selectedRole !== originalRole;

      // Preparar dados do usuário no formato UserDto
      const userDto = {
        id: selectedUser.id,
        userName: selectedUser.username || selectedUser.userName,
        
        fullName: selectedUser.fullName || '',
        email: selectedUser.email,
        phoneNumber: selectedUser.phoneNumber || '',
        active: selectedUser.active !== false,
        files: selectedUser.files || null,
        permissions: permissions,
        granularPermissions: granularPermissionsArray,
        role: selectedRole ,
        UpdateRoles: roleChanged,
        tenantId: selectedUser.tenantId || getUserInfo()?.tenantId || null
      };

      // Chamar API para salvar permissões
      const response = await Api.post('/user/upsert', userDto);
      
      if (response.status === 200) {
        Toast.success(`Permissões do usuário ${displayUsername} atualizadas com sucesso`);
        
        setHasChanges(false);
        
        // Chamar callback para atualizar usuários na tabela
        if (onUsersUpdate) {
          console.log('🔄 UserPermissionsModal - Chamando onUsersUpdate');
          await onUsersUpdate();
        }
        
        onCancel();
        
      } else {
        throw new Error('Erro na resposta da API');
      }
      
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      notification.error({
        message: 'Erro',
        description: 'Erro ao salvar permissões do usuário.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calcular estatísticas
  const allPermissions = FEATURES_LIST.reduce((total, feature) => total + feature.actions.length, 0);
  const grantedPermissions = Object.values(userPermissions).filter(Boolean).length;
  const granularCount = Object.values(granularPermissions).reduce((total, arr) => total + arr.length, 0);

  const getPermissionLevel = () => {
    const percentage = (grantedPermissions / allPermissions) * 100;
    if (percentage === 100) return { text: 'Total', color: theme.success };
    if (percentage >= 75) return { text: 'Alto', color: theme.warning };
    if (percentage >= 50) return { text: 'Médio', color: theme.warning };
    return { text: 'Baixo', color: theme.error };
  };

  const permissionLevel = getPermissionLevel();

  const displayUsername = selectedUser?.username || selectedUser?.userName;
  const modalSurface = theme.cardBackground || theme.backgroundSecondary;
  const sectionSurface = theme.backgroundSecondary || theme.cardBackground;

  return (
    <Modal
      title={
        <Space>
          <Avatar 
            size="small" 
            icon={<UserOutlined />} 
            style={{ backgroundColor: theme.primary }}
          />
          <span>Permissões de {displayUsername}</span>
          {isSuperAdmin(selectedUser) && (
            <Tag color={theme.primary}>Administrador</Tag>
          )}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      width={900}
      style={{ top: 20, borderRadius: '12px' }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={loading}
            style={{ backgroundColor: theme.primary, color: theme.white }}

            disabled={!hasChanges}
            icon={<SaveOutlined />}
          >
            Salvar Alterações
          </Button>
        </div>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Informações do usuário */}
        <Card 
          size="small" 
          style={{ backgroundColor: modalSurface }}
          title="Informações do Usuário"
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, alignItems: 'center', marginBottom: 8, justifyContent: 'center', textAlign: 'center' }}>
              <div>
              <Text strong>Usuário:</Text>
              <div><Text>{displayUsername}</Text></div>
            </div>
            <div>
              <Text strong>Tipo de Usuário:</Text>
              <div >
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  options={roleOptions}
                  style={{ width: '60%' }}
                  placeholder="Selecione o tipo de usuário"
                />
              </div>
            </div>
            <div>
              <Text strong>Último Acesso:</Text>
              <div><Text>{selectedUser?.ultimoAcesso ? formatDate(selectedUser.ultimoAcesso) : 'Primeiro acesso'}</Text></div>
            </div>
          </div>
          
          
        </Card>

        {/* Resumo das permissões */}
        <Card title="Resumo de Permissões" size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Nível de Acesso</Text>
              <Tag color={permissionLevel.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
                {permissionLevel.text}
              </Tag>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Permissões Tradicionais</Text>
              <Text style={{ fontSize: '16px', color: theme.primary }}>
                {grantedPermissions}/{allPermissions}
              </Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Acessos Específicos</Text>
              <Text style={{ fontSize: '16px', color: theme.primary }}>
                {granularCount} recursos
              </Text>
            </div>
          </div>
        </Card>

        {/* Permissões por seções */}
        <Collapse
          style={{
            backgroundColor: modalSurface,
            border: `1px solid ${theme.border}`
          }}
        >
          {/* Permissões Granulares de Projetos */}
          <Panel
            header="Acesso a Projetos Específicos"
            key="1"
            style={{
              backgroundColor: sectionSurface,
              borderBottom: `1px solid ${theme.border}`
            }}
          >
            <Card size="small" title="Projetos Disponíveis">
              <Text style={{ display: 'block', marginBottom: 16, color: theme.textSecondary }}>
                Selecione os projetos que o usuário pode acessar e gerenciar. O usuário terá acesso total aos projetos selecionados.
              </Text>
              
              <Select
                mode="multiple"
                placeholder="Selecione os projetos..."
                value={granularPermissions.projects}
                onChange={(value) => handleGranularPermissionChange('projects', value)}
                style={{ width: '100%', marginBottom: 16 }}
                disabled={isSuperAdmin(selectedUser)}
                loading={projectsLoading}
                options={availableProjects.map(project => ({
                  label: (
                    <div>
                      <div style={{ fontWeight: 500 }}>{project.name}</div>
                      <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                        {project.type} 
                      </div>
                    </div>
                  ),
                  value: project.id
                }))}
              />
              
              {granularPermissions.projects.length > 0 && (
                <div>
                  <Text strong style={{ marginBottom: 8, display: 'block' }}>
                    Projetos Selecionados ({granularPermissions.projects.length}):
                  </Text>
                  <Space wrap>
                    {granularPermissions.projects.map(projectId => {
                      const project = availableProjects.find(p => p.id === projectId);
                      return project ? (
                        <Tag key={projectId} color="purple">
                          {project.name}
                        </Tag>
                      ) : null;
                    })}
                  </Space>
                </div>
              )}
            </Card>
          </Panel>
          {/* Permissões Tradicionais */}
          <Panel
            header="Permissões de Módulos"
            key="2"
            style={{
              backgroundColor: sectionSurface,
              borderBottom: `1px solid ${theme.border}`
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {FEATURES_LIST.map(feature => (
                <Card 
                  key={feature.id} 
                  size="small" 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong>{feature.name}</Text>
                        {getModuleSwitchState(feature) === 'indeterminate' && (
                          <Tag size="small" color="orange">Parcial</Tag>
                        )}
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          ({feature.actions.filter(action => userPermissions[action.id]).length}/{feature.actions.length})
                        </Text>
                      </div>
                      <Tooltip
                        title={
                          <div style={{ fontSize: '12px' }}>
                            <p>Clique para alternar todas as permissões deste módulo.</p>
                            <p>
                              <strong>Verde:</strong> Todas as permissões estão ativadas.
                            </p>
                            <p>
                              <strong>Laranja:</strong> Algumas permissões estão ativadas.
                            </p>
                            <p>
                              <strong>Vermelho:</strong> Nenhuma permissão está ativada.
                            </p>
                          </div>
                        }
                      >
                        <SwitchInput
                          size="small"
                          checked={getModuleSwitchState(feature) === true}
                          onChange={(value) => handleModulePermissionChange(feature, value)}
                          disabled={isSuperAdmin(selectedUser)}
                        />
                      </Tooltip>
                    </div>
                  }
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {feature.actions.map(action => (
                      <div key={action.id} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '4px 0'
                      }}>
                        <Text>{action.name}</Text>
                        <SwitchInput
                          size="small"
                          checked={userPermissions[action.id] || false}
                          onChange={(value) => handlePermissionChange(action.id, value)}
                          disabled={isSuperAdmin(selectedUser)}
                        />
                      </div>
                    ))}
                  </Space>
                </Card>
              ))}

              <Card
                key="ui-gestao-menus"
                size="small"
                title={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Text strong>Gestão</Text>
                      {gestaoSwitchState === "indeterminate" && (
                        <Tag size="small" color="orange">Parcial</Tag>
                      )}
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ({gestaoEnabledCount}/{GESTAO_MENUS.length})
                      </Text>
                    </div>
                    <Tooltip
                      title={
                        <div style={{ fontSize: "12px" }}>
                          <p>Clique para alternar todos os menus do Gestão.</p>
                          <p>
                            <strong>Verde:</strong> Todos os menus estão liberados.
                          </p>
                          <p>
                            <strong>Laranja:</strong> Alguns menus estão liberados.
                          </p>
                          <p>
                            <strong>Vermelho:</strong> Nenhum menu está liberado.
                          </p>
                        </div>
                      }
                    >
                      <SwitchInput
                        size="small"
                        checked={gestaoSwitchState === true}
                        onChange={(value) => toggleAllGestaoMenus(value)}
                        disabled={isSuperAdmin(selectedUser)}
                      />
                    </Tooltip>
                  </div>
                }
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {GESTAO_MENUS.map((menu) => (
                    <div
                      key={menu.key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 0",
                      }}
                    >
                      <Text>{menu.label}</Text>
                      <SwitchInput
                        size="small"
                        checked={getGestaoMenuEnabled(menu.key)}
                        onChange={(value) => setGestaoMenuEnabled(menu.key, value)}
                        disabled={isSuperAdmin(selectedUser)}
                      />
                    </div>
                  ))}
                </Space>
              </Card>

              {tenantHasStudio && (
                <Card
                  key="ui-studio-access"
                  size="small"
                  title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Text strong>Studio</Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          ({studioAllowed ? 1 : 0}/1)
                        </Text>
                      </div>
                      <Tooltip title="Clique para permitir ou bloquear o acesso ao Studio para este usuário.">
                        <SwitchInput
                          size="small"
                          checked={studioAllowed}
                          onChange={(value) => setStudioAllowed(value)}
                          disabled={isSuperAdmin(selectedUser)}
                        />
                      </Tooltip>
                    </div>
                  }
                >
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 0",
                      }}
                    >
                      <Text>Acesso ao Studio</Text>
                      <SwitchInput
                        size="small"
                        checked={studioAllowed}
                        onChange={(value) => setStudioAllowed(value)}
                        disabled={isSuperAdmin(selectedUser)}
                      />
                    </div>
                  </Space>
                </Card>
              )}

              <Card
                key="ui-academy-access"
                size="small"
                title={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Text strong>Academy</Text>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ({academyAllowed ? 1 : 0}/1)
                      </Text>
                    </div>
                    <Tooltip title="Clique para permitir ou bloquear o acesso ao Academy para este usuário.">
                      <SwitchInput
                        size="small"
                        checked={academyAllowed}
                        onChange={(value) => setAcademyAllowed(value)}
                        disabled={isSuperAdmin(selectedUser)}
                      />
                    </Tooltip>
                  </div>
                }
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 0",
                    }}
                  >
                    <Text>Acesso ao Academy</Text>
                    <SwitchInput
                      size="small"
                      checked={academyAllowed}
                      onChange={(value) => setAcademyAllowed(value)}
                      disabled={isSuperAdmin(selectedUser)}
                    />
                  </div>
                </Space>
              </Card>
            </div>
          </Panel>
        </Collapse>

        {hasChanges && (
          <Alert
            message="Alterações Pendentes"
            description="Você tem alterações não salvas. Clique em 'Salvar Alterações' para confirmar."
            type="warning"
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};

export default UserPermissionsModal; 
